import React from "react";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { fmtINR } from "../../lib/api";

/**
 * Phase 207 — editable final demand lines (Tax head · FY · Arrear ₹ · Current ₹), multiple rows.
 * Used by the Field Collector (enter final demand), Supervisor and Municipal Admin (modify before approving).
 */
export const EMPTY_LINE = { tax_head_id: "", financial_year_id: "", usage_label: "", arrear_amount: "", current_amount: "" };

export const linesToEditor = (lines) => (lines || []).map((ln) => ({
  ...EMPTY_LINE, ...ln,
  arrear_amount: ln.arrear_amount != null && ln.arrear_amount !== "" ? String(ln.arrear_amount) : "",
  current_amount: ln.current_amount != null && ln.current_amount !== "" ? String(ln.current_amount) : "",
  usage_label: ln.usage_label || "",
}));

export const linesPayload = (lines, defaultFy) => (lines || [])
  .filter((ln) => ln.tax_head_id && ((Number(ln.arrear_amount) || 0) > 0 || (Number(ln.current_amount) || 0) > 0))
  .map((ln) => ({ tax_head_id: ln.tax_head_id, financial_year_id: ln.financial_year_id || defaultFy || null,
    usage_label: (ln.usage_label || "").trim() || null, arrear_amount: Number(ln.arrear_amount) || 0, current_amount: Number(ln.current_amount) || 0 }));

export const linesTotal = (lines) => (lines || []).reduce((a, ln) => ({
  arrear: a.arrear + (Number(ln.arrear_amount) || 0), current: a.current + (Number(ln.current_amount) || 0),
}), { arrear: 0, current: 0 });

export function SafDemandLinesEditor({ lines, setLines, taxHeads = [], fys = [], defaultFy = "", readOnly = false, testIdPrefix = "saf-line",
  title = "Final demand lines", hint = "One row per tax head. Enter the arrear (previous years) and the current year amount in rupees." }) {
  const set = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const tot = linesTotal(lines);
  const headName = (id) => taxHeads.find((h) => h.id === id)?.tax_head_name || null;
  const fyName = (id) => fys.find((f) => f.id === id)?.fy_label || null;
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      <div>
        <Label className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> {title}</Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] text-muted-foreground px-1">
        <span className="col-span-4">Tax head</span><span className="col-span-3">Financial year</span>
        <span className="col-span-2 text-right">Arrear ₹</span><span className="col-span-2 text-right">Current ₹</span><span className="col-span-1" />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center border rounded-lg p-2 sm:border-0 sm:p-0" data-testid={`${testIdPrefix}-${i}`}>
          <div className="col-span-2 sm:col-span-4">
            <Select value={l.tax_head_id || ""} onValueChange={(v) => v && set(i, "tax_head_id", v)} disabled={readOnly}>
              <SelectTrigger className="h-10" data-testid={`${testIdPrefix}-head-${i}`} aria-label="Tax head">
                <SelectValue placeholder="Tax head *">{headName(l.tax_head_id) || l.tax_head_name || null}</SelectValue>
              </SelectTrigger>
              <SelectContent>{taxHeads.map((h) => <SelectItem key={h.id} value={h.id}>{h.tax_head_name}</SelectItem>)}</SelectContent>
            </Select>
            {l.usage_label ? <p className="text-[11px] text-muted-foreground px-1 mt-0.5 truncate">{l.usage_label}</p> : null}
          </div>
          <div className="col-span-2 sm:col-span-3">
            <Select value={l.financial_year_id || defaultFy || ""} onValueChange={(v) => v && set(i, "financial_year_id", v)} disabled={readOnly}>
              <SelectTrigger className="h-10" data-testid={`${testIdPrefix}-fy-${i}`} aria-label="Financial year">
                <SelectValue placeholder="FY">{fyName(l.financial_year_id || defaultFy) || l.fy_label || null}</SelectValue>
              </SelectTrigger>
              <SelectContent>{fys.map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}{f.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input className="h-10 sm:col-span-2 text-right" type="number" step="any" min="0" inputMode="decimal" placeholder="Arrear ₹" value={l.arrear_amount}
            disabled={readOnly} onChange={(e) => set(i, "arrear_amount", e.target.value)} data-testid={`${testIdPrefix}-arrear-${i}`} aria-label="Arrear amount" />
          <Input className="h-10 sm:col-span-2 text-right" type="number" step="any" min="0" inputMode="decimal" placeholder="Current ₹" value={l.current_amount}
            disabled={readOnly} onChange={(e) => set(i, "current_amount", e.target.value)} data-testid={`${testIdPrefix}-current-${i}`} aria-label="Current amount" />
          <div className="col-span-2 sm:col-span-1 flex justify-end">
            {!readOnly && lines.length > 1 && (
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                aria-label="Remove line" data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            )}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        {!readOnly ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE, financial_year_id: defaultFy }])} data-testid={`${testIdPrefix}-add`}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add demand line
          </Button>
        ) : <span />}
        <div className="text-sm text-right" data-testid={`${testIdPrefix}-totals`}>
          <span className="text-muted-foreground text-xs mr-3">Arrear {fmtINR(tot.arrear)} · Current {fmtINR(tot.current)}</span>
          <span className="font-semibold tabular-nums">Total demand {fmtINR(tot.arrear + tot.current)}</span>
        </div>
      </div>
    </div>
  );
}
